import {
  Input,
  Component,
  SimpleChanges,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
  Output,
  EventEmitter,
  HostListener,
  ElementRef,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { NomMassUpdateOperation } from '../../../constants';
import { updatePostsUserCategories } from '../../../actions/post.actions';
import { realPostId } from 'src/app/shared/utils/shadow-post.utils';

import { AppState } from 'src/app/reducers';
import { selectFeedCategoryTree } from 'src/app/core/reducers';

import findIndex from 'lodash-es/findIndex';
import remove from 'lodash-es/remove';
import { tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-post-category-tree',
  templateUrl: './post-category-tree.component.html',
  styleUrl: './post-category-tree.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PostCategoryTreeComponent {
  @Input() postId: string;
  @Input() selectedCategories: any[];
  // optional: only check outside clicks when open
  @Input() open = true;

  // parent can hide/destroy the component when this fires
  @Output() requestClose = new EventEmitter<void>();

  categoryTree: any;
  selectedCategoriesCopy: any[];

  categories: { [key: number]: number[] } = {};
  updateOperation: NomMassUpdateOperation = NomMassUpdateOperation.EDIT;
  private destroyRef = inject(DestroyRef);
  private el = inject(ElementRef<HTMLElement>);

  constructor(private store: Store<AppState>) {
    this.store
      .select(selectFeedCategoryTree)
      .pipe(
        tap((categoryTree) => {
          this.categoryTree = categoryTree;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  // Close on outside click
  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.open) return;
    const target = event.target as Node | null;
    if (target && !this.el.nativeElement.contains(target)) {
      this.requestClose.emit();
    }
  }

  isSelected(id: number) {
    return this.selectedCategoriesCopy.some((category) => category?.id === id);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedCategories']) {
      this.selectedCategoriesCopy = [
        ...(changes['selectedCategories'].currentValue || []),
      ];
    }
  }

  toggleCheckbox(category: any) {
    this.categories = {};

    const categoryIndex = findIndex(this.selectedCategoriesCopy, {
      id: category.id,
    });

    if (categoryIndex !== -1) {
      remove(this.selectedCategoriesCopy, { id: category.id });
    } else {
      this.selectedCategoriesCopy.push(category);
      this.addParentCategory(category);
    }

    this.constructCategoriesArray();

    if (this.categories && this.postId) {
      // Thread "shadow" posts carry a synthetic id (`${realId}__shadow__${leafId}`);
      // categorization must target the real post id, never the display-only shadow id.
      this.store.dispatch(
        updatePostsUserCategories({
          postIds: [realPostId(this.postId)],
          categories: this.categories,
          updateOperation: NomMassUpdateOperation.EDIT,
        }),
      );
    }
  }

  private addParentCategory(category: any) {
    if (category.parent_user_category_id !== 0) {
      this.categoryTree.forEach((element) => {
        const parentElement = element.categories.find(
          (item) => item.id === category.parent_user_category_id,
        );
        if (parentElement) {
          this.selectedCategoriesCopy.push(parentElement);
        }
      });
    }
  }

  private constructCategoriesArray() {
    this.selectedCategoriesCopy.forEach((category) => {
      const companyCategoryId = category?.company_category_id;

      if (!this.categories[companyCategoryId]) {
        this.categories[companyCategoryId] = [];
      }

      if (!this.categories[companyCategoryId].includes(category?.id)) {
        this.categories[companyCategoryId].push(category.id);
      }
    });
  }
}
